import React, { useState, useEffect } from 'react';
import { useToast, ToastContainer } from '../common/Toast';

const DevConfig: React.FC = () => {
  const { toasts, showSuccess, showError } = useToast();
  const [useSimulation, setUseSimulation] = useState(false);
  const [apiBaseUrl, setApiBaseUrl] = useState('');
  const [apiVersion, setApiVersion] = useState('');
  const [apiStatus, setApiStatus] = useState<'unknown' | 'testing' | 'available' | 'unavailable'>('unknown');

  useEffect(() => {
    // Charger la configuration depuis le localStorage
    const savedSimulation = localStorage.getItem('dev_use_simulation');
    if (savedSimulation !== null) {
      setUseSimulation(JSON.parse(savedSimulation));
    } else {
      // Par défaut, activer la simulation si aucun paramètre n'est sauvegardé
      setUseSimulation(true);
      localStorage.setItem('dev_use_simulation', 'true');
    }

    // Charger la configuration API
    setApiBaseUrl((import.meta as any).env?.VITE_API_BASE_URL || 'https://api.local.ci');
    setApiVersion('v1');
  }, []);

  const toggleSimulation = () => {
    const newValue = !useSimulation;
    setUseSimulation(newValue);
    localStorage.setItem('dev_use_simulation', JSON.stringify(newValue));
    
    if (newValue) {
      showSuccess('Simulation activée', 'L\'assignation de sites utilisera la simulation en mode développement');
    } else {
      showSuccess('Simulation désactivée', 'L\'assignation de sites tentera d\'utiliser l\'API réelle');
    }
  };

  const testApiConnection = async () => {
    setApiStatus('testing');
    
    try {
      const response = await fetch(`${apiBaseUrl}/api/${apiVersion}/sites/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      if (response.ok) {
        setApiStatus('available');
        showSuccess('Connexion API OK', `Endpoint ${response.url} accessible (${response.status})`);
      } else {
        setApiStatus('unavailable');
        showError('Connexion API échouée', `Endpoint ${response.url} retourne ${response.status}`);
      }
    } catch (error: any) {
      setApiStatus('unavailable');
      showError('Erreur de connexion', `Impossible de se connecter à l'API: ${error.message}`);
    }
  };

  const clearDevData = () => {
    if (confirm('Êtes-vous sûr de vouloir effacer toutes les données de développement ?')) {
      localStorage.removeItem('dev_use_simulation');
      
      // Effacer toutes les assignations simulées
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('sim_assignments_') || key.startsWith('sim_sites') || key.startsWith('sim_roles'))) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      setUseSimulation(false);
      showSuccess('Données effacées', `Toutes les données de développement ont été effacées (${keysToRemove.length} éléments supprimés)`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">⚙️ Configuration Développement</h1>
          
          <div className="space-y-6">
            {/* Configuration de la simulation */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-blue-900 mb-3">🎭 Mode Simulation</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-blue-700">
                    Utiliser la simulation pour l'assignation de sites
                  </span>
                  <button
                    onClick={toggleSimulation}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      useSimulation ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        useSimulation ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                
                <div className="text-xs text-blue-600">
                  {useSimulation ? (
                    <p>✅ <strong>Simulation activée</strong> - L'assignation de sites sera simulée</p>
                  ) : (
                    <p>❌ <strong>Simulation désactivée</strong> - L'assignation de sites utilisera l'API réelle</p>
                  )}
                </div>
              </div>
            </div>

            {/* Configuration de l'API */}
            <div className="bg-green-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-green-900 mb-3">🔗 Configuration API</h3>
              <div className="space-y-2 text-sm text-green-700">
                <p><strong>Base URL:</strong> {apiBaseUrl}</p>
                <p><strong>Version:</strong> {apiVersion}</p>
                <p><strong>URL complète:</strong> {apiBaseUrl}/api/{apiVersion}/</p>
                <p><strong>Statut:</strong> 
                  <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                    apiStatus === 'available' ? 'bg-green-100 text-green-800' :
                    apiStatus === 'unavailable' ? 'bg-red-100 text-red-800' :
                    apiStatus === 'testing' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {apiStatus === 'available' ? '✅ Disponible' :
                     apiStatus === 'unavailable' ? '❌ Indisponible' :
                     apiStatus === 'testing' ? '🔄 Test en cours' :
                     '❓ Non testé'}
                  </span>
                </p>
              </div>
              
              <button
                onClick={testApiConnection}
                disabled={apiStatus === 'testing'}
                className="mt-3 inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {apiStatus === 'testing' ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Test en cours...
                  </>
                ) : (
                  '🧪 Tester la connexion API'
                )}
              </button>
            </div>

            {/* Instructions */}
            <div className="bg-yellow-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-yellow-900 mb-2">📋 Instructions</h3>
              <div className="text-sm text-yellow-800 space-y-2">
                <p><strong>Mode Simulation activé :</strong></p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>L'assignation de sites sera simulée</li>
                  <li>Aucun appel API réel ne sera effectué</li>
                  <li>Parfait pour le développement et les tests</li>
                  <li>Les données ne seront pas persistées</li>
                </ul>
                
                <p className="mt-3"><strong>Mode Simulation désactivé :</strong></p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>L'assignation de sites utilisera l'API réelle</li>
                  <li>Les données seront persistées côté serveur</li>
                  <li>Requiert que l'endpoint d'assignation soit implémenté</li>
                </ul>
              </div>
            </div>

            {/* Actions de développement */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-3">🛠️ Actions de Développement</h3>
              <div className="space-y-3">
                <button
                  onClick={clearDevData}
                  className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
                >
                  🗑️ Effacer les données de développement
                </button>
                
                <div className="text-xs text-gray-600">
                  <p>• Efface toutes les préférences de développement</p>
                  <p>• Remet la configuration par défaut</p>
                  <p>• Utile pour tester depuis un état propre</p>
                </div>
              </div>
            </div>

            {/* Liens utiles */}
            <div className="bg-purple-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-purple-900 mb-2">🔗 Liens utiles</h3>
              <div className="text-sm text-purple-700 space-y-2">
                <p>• <a href="/administration/api-diagnostic" className="text-purple-600 hover:text-purple-800 underline">Diagnostic complet de l'API</a></p>
                <p>• <a href="/administration/simple-assign-test" className="text-purple-600 hover:text-purple-800 underline">Test simple d'assignation</a></p>
                <p>• <a href="/administration/user/5" className="text-purple-600 hover:text-purple-800 underline">Page de détail utilisateur</a></p>
                <p>• <a href="/administration/test-assign-site" className="text-purple-600 hover:text-purple-800 underline">Test complet d'assignation</a></p>
              </div>
            </div>

            {/* Recommandations basées sur le statut */}
            {apiStatus !== 'unknown' && (
              <div className={`rounded-lg p-4 ${
                apiStatus === 'available' ? 'bg-green-50 border border-green-200' :
                apiStatus === 'unavailable' ? 'bg-red-50 border border-red-200' :
                'bg-yellow-50 border border-yellow-200'
              }`}>
                <h3 className={`text-lg font-medium mb-3 ${
                  apiStatus === 'available' ? 'text-green-900' :
                  apiStatus === 'unavailable' ? 'text-red-900' :
                  'text-yellow-900'
                }`}>
                  {apiStatus === 'available' ? '✅ Recommandations' :
                   apiStatus === 'unavailable' ? '⚠️ Recommandations' :
                   '🔄 Test en cours'}
                </h3>
                
                <div className={`text-sm space-y-2 ${
                  apiStatus === 'available' ? 'text-green-800' :
                  apiStatus === 'unavailable' ? 'text-red-800' :
                  'text-yellow-800'
                }`}>
                  {apiStatus === 'available' && (
                    <>
                      <p>• L'API est accessible et fonctionne correctement</p>
                      <p>• Vous pouvez désactiver la simulation pour tester l'API réelle</p>
                      <p>• Vérifiez que l'endpoint d'assignation est implémenté</p>
                    </>
                  )}
                  
                  {apiStatus === 'unavailable' && (
                    <>
                      <p>• L'API n'est pas accessible ou retourne des erreurs</p>
                      <p>• Gardez la simulation activée pour le développement</p>
                      <p>• Vérifiez la configuration de l'API et la connexion réseau</p>
                      <p>• Contactez l'équipe backend pour résoudre les problèmes</p>
                    </>
                  )}
                  
                  {apiStatus === 'testing' && (
                    <>
                      <p>• Test de connexion en cours...</p>
                      <p>• Veuillez patienter pendant la vérification</p>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Container des toasts */}
      <ToastContainer toasts={toasts} position="top-right" />
    </div>
  );
};

export default DevConfig;
